import type { ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  LucideIcon,
  TrendingUp,
} from 'lucide-react';
import { ProfileHighlightSummary, TransitEvaluation } from '../../api';

// Modelo minimo partilhado entre a Insights (Pro) e a InsightsLite.
// Estruturalmente compativel com InsightsLiteCalendarDayDto, mas desacoplado
// para que o Pro possa construir os dias a partir dos seus proprios endpoints.
export interface CalendarDay {
  date?: Date;
  bestProfileKey?: string;
  bestProfileScore?: number;
  cautionProfileKey?: string;
  cautionProfileScore?: number;
  averageScore?: number;
}

// Contrato de props da base partilhada de insights (PL-002 - padrao aditivo).
export interface InsightsBaseProps {
  days: CalendarDay[];
  titleKey?: string;
  descriptionKey?: string;
  children?: ReactNode;
}

export type DayTone = {
  border: string;
  bg: string;
  text: string;
  icon: LucideIcon;
  labelKey: string;
};

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getScore = (value: number | undefined): number => Math.round(value ?? 0);

// Um dia so tem informacao de calendario se tiver pelo menos um score.
export const hasCalendarScore = (day: CalendarDay): boolean =>
  typeof day.bestProfileScore === 'number' ||
  typeof day.cautionProfileScore === 'number' ||
  typeof day.averageScore === 'number';

export const getDayScore = (day: CalendarDay): number =>
  getScore(day.averageScore ?? day.bestProfileScore ?? day.cautionProfileScore);

export const getDayTone = (score: number): DayTone => {
  if (score >= 70) {
    return {
      border: 'border-emerald-300',
      bg: 'bg-emerald-50',
      text: 'text-emerald-800',
      icon: CheckCircle2,
      labelKey: 'insightsLiteStrongDay',
    };
  }

  if (score <= 45) {
    return {
      border: 'border-rose-300',
      bg: 'bg-rose-50',
      text: 'text-rose-800',
      icon: AlertTriangle,
      labelKey: 'insightsLiteCautionDay',
    };
  }

  return {
    border: 'border-sky-200',
    bg: 'bg-sky-50',
    text: 'text-sky-800',
    icon: TrendingUp,
    labelKey: 'insightsLiteBalancedDay',
  };
};

// Tom neutro para dias sem dados (usado pelo Pro quando os highlights nao
// cobrem esse dia). Evita pintar dias sem informacao como "cautela".
export const NEUTRAL_TONE = {
  border: 'border-stone-200',
  bg: 'bg-stone-50',
  text: 'text-slate-400',
};

export const formatShortDate = (date: Date | undefined, language: string): string =>
  date
    ? date.toLocaleDateString(language, {
        day: '2-digit',
        month: 'short',
      })
    : '-';

export const formatWeekday = (date: Date | undefined, language: string): string =>
  date
    ? date.toLocaleDateString(language, {
        weekday: 'short',
      })
    : '-';

export const getMondayFirstWeekdayIndex = (date: Date): number => (date.getDay() + 6) % 7;

// Abreviaturas dos dias da semana (segunda-feira primeiro), como chaves i18n.
export const WEEKDAY_ABBREVIATION_KEYS = [
  'mondayAbbreviation',
  'tuesdayAbbreviation',
  'wednesdayAbbreviation',
  'thursdayAbbreviation',
  'fridayAbbreviation',
  'saturdayAbbreviation',
  'sundayAbbreviation',
];

const enumerateDays = (start: Date, end: Date): Date[] => {
  const days: Date[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(0, 0, 0, 0);

  while (cursor <= last) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

// Deriva os dias de calendario (formato Lite) a partir dos highlights Pro.
// O perfil "positive" fornece o melhor perfil/score do dia; o "negative"
// fornece o perfil/score de cautela. Dias sem highlights ficam sem score.
export const deriveCalendarDays = (
  highlights: ProfileHighlightSummary | null | undefined,
  start: Date,
  end: Date,
): CalendarDay[] => {
  const days = enumerateDays(start, end).map<CalendarDay>((date) => ({ date }));
  const byKey = new Map<string, CalendarDay>();
  days.forEach((day) => {
    if (day.date) {
      byKey.set(formatLocalDate(day.date), day);
    }
  });

  const applyEvaluations = (
    profileKey: string | undefined,
    evaluations: TransitEvaluation[] | undefined,
    kind: 'best' | 'caution',
  ) => {
    (evaluations ?? []).forEach((evaluation) => {
      if (!evaluation.date) {
        return;
      }

      const day = byKey.get(formatLocalDate(evaluation.date));
      if (!day) {
        return;
      }

      const score = getScore(evaluation.percentAbsolute);

      if (kind === 'best') {
        if (day.bestProfileScore === undefined || score > day.bestProfileScore) {
          day.bestProfileScore = score;
          day.bestProfileKey = profileKey;
        }
        return;
      }

      if (day.cautionProfileScore === undefined || score < day.cautionProfileScore) {
        day.cautionProfileScore = score;
        day.cautionProfileKey = profileKey;
      }
    });
  };

  applyEvaluations(highlights?.positive?.profileKey, highlights?.positive?.transitEvaluations, 'best');
  applyEvaluations(highlights?.negative?.profileKey, highlights?.negative?.transitEvaluations, 'caution');

  days.forEach((day) => {
    const parts = [day.bestProfileScore, day.cautionProfileScore].filter(
      (value): value is number => typeof value === 'number',
    );
    day.averageScore = parts.length
      ? Math.round(parts.reduce((sum, value) => sum + value, 0) / parts.length)
      : undefined;
  });

  return days;
};
